"use client";

import { useState } from "react";
import { getGroupEventsByDate } from "@/lib/helpers";
import { parseShowCalendarDate } from "@/lib/showDates";
import { Card, CardContent } from "@/components/ui/card";
import { Icon } from "@/components";
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
} from "lucide-react";
import { useKindeSession } from "@/components/KindeWrapper";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useIsLargeScreen } from "@/lib/helpers";
import { EventDetails, Img } from "@/components";

// Local helper functions
function formatDate(dateString) {
  const date = parseShowCalendarDate(dateString);
  if (!date) return "";

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const dayOfWeek = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayOfMonth = date.getDate();

  return `${dayOfWeek} ${dayOfMonth} ${monthName}`;
}

function formatTime(timeString) {
  if (!timeString) return "";

  // If timeString is already a formatted time (like "14:30"), convert it
  if (typeof timeString === "string" && timeString.includes(":")) {
    const [hour, minute] = timeString.split(":");
    const hourIn12HourFormat = hour % 12 || 12;
    const period = hour < 12 ? "AM" : "PM";
    return `${hourIn12HourFormat}:${minute} ${period}`;
  }

  // If it's a Date object
  if (timeString instanceof Date) {
    const hours = timeString.getHours();
    const minutes = timeString.getMinutes();
    const hourIn12HourFormat = hours % 12 || 12;
    const period = hours < 12 ? "AM" : "PM";
    return `${hourIn12HourFormat}:${minutes
      .toString()
      .padStart(2, "0")} ${period}`;
  }

  return timeString;
}

function getTimeInMinutes(timeString) {
  if (typeof timeString !== "string") return null;

  // Statamic normally returns 24-hour HH:mm values, but accept unpadded and
  // 12-hour values too so the display order is not dependent on CMS formatting.
  const match = timeString
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap]m)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3]?.toLowerCase();

  if (minutes > 59) return null;

  if (period) {
    if (hours < 1 || hours > 12) return null;
    hours = hours % 12 + (period === "pm" ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }

  return hours * 60 + minutes;
}

function sortEventsByStartTime(events) {
  return [...events].sort((a, b) => {
    // All-day entries do not have a specific place in a timed schedule, so
    // retain the existing behaviour of placing them after timed events.
    if (a.all_day && !b.all_day) return 1;
    if (!a.all_day && b.all_day) return -1;
    if (a.all_day && b.all_day) return 0;

    const aStartTime = getTimeInMinutes(a.start_time);
    const bStartTime = getTimeInMinutes(b.start_time);

    if (aStartTime === null && bStartTime === null) return 0;
    if (aStartTime === null) return 1;
    if (bStartTime === null) return -1;
    return aStartTime - bStartTime;
  });
}

export function SubEvents({ events, mapIsActive, showDates }) {
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const eventsByDate = getGroupEventsByDate(events, showDates);
  const { isLargeScreen } = useIsLargeScreen();
  const { user } = useKindeSession();
  const userId = user?.id || null;

  const toggleDay = (dateKey) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(dateKey)) {
      newExpandedDays.delete(dateKey);
    } else {
      newExpandedDays.add(dateKey);
    }
    setExpandedDays(newExpandedDays);
  };

  const handleEventClick = (eventTime, parentEvent) => {
    // Create a proper image object structure
    const imageData = eventTime.image?.permalink
      ? {
          permalink: eventTime.image.permalink,
          alt:
            eventTime.image.alt ||
            eventTime.override_title ||
            parentEvent?.title,
        }
      : parentEvent?.grid_image?.permalink
      ? {
          permalink: parentEvent.grid_image.permalink,
          alt: parentEvent.grid_image.alt || parentEvent?.title,
        }
      : null;

    const eventTimeData = {
      ...eventTime,
      showEndTime: !eventTime.hide_end_time,
    };

    // Create a unique slug for this specific sub-event using parent slug + index
    const createSubEventSlug = (parentSlug, eventTime, parentEvent) => {
      // Find the index of this sub-event in the parent's days array
      const dayIndex = parentEvent.days?.findIndex(
        (day) =>
          day.date === eventTime.date &&
          day.start_time === eventTime.start_time &&
          day.end_time === eventTime.end_time
      );

      // If we can't find the index, fall back to a timestamp-based approach
      if (dayIndex === -1 || dayIndex === undefined) {
        return `${parentSlug}-sub-${Date.now()}`;
      }

      return `${parentSlug}-sub-${dayIndex}`;
    };

    const subEventSlug = createSubEventSlug(
      parentEvent.slug,
      eventTime,
      parentEvent
    );

    setSelectedEvent({
      data: {
        ...parentEvent,
        slug: subEventSlug, // Use the unique sub-event slug
        url: parentEvent.url, // Keep the parent event's URL for navigation
        short_description:
          eventTime.override_short || parentEvent?.short_description,
      },
      eventTime: eventTimeData,
      image: imageData,
      title: eventTime.override_title || parentEvent?.title,
      type: "events",
    });
    setIsModalOpen(true);
  };

  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    const dateA = parseShowCalendarDate(a);
    const dateB = parseShowCalendarDate(b);
    if (!dateA || !dateB) return 0;
    return dateA - dateB;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-left">
        <h3 className="level-2">Daily Schedule</h3>
        <p className="text-base text-muted-foreground">
          Click on any day to view its events
        </p>
      </div>

      <div className="space-y-3">
        {sortedDates.map((dateKey) => {
          const events = eventsByDate[dateKey];
          const sortedEvents = sortEventsByStartTime(events);
          const isExpanded = expandedDays.has(dateKey);
          const eventCount = events.length;

          return (
            <div
              key={dateKey}
              className="overflow-hidden bg-white border-2 shadow-lg border-foreground/20 rounded-card-radius"
            >
              {/* Day Header - Clickable */}
              <button
                onClick={() => toggleDay(dateKey)}
                className="flex justify-between items-center p-4 w-full transition-all duration-200 lg:p-6 bg-white text-card-foreground group"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 shadow-sm bg-background rounded-base text-brand-red-gum">
                      <Icon icon="events" size={20} strokeWidth={1.7} />
                    </div>
                    <div className="text-left">
                      <h4 className="transition-colors level-3 group-hover:text-brand-red-gum">
                        {formatDate(dateKey)}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {eventCount} event{eventCount !== 1 ? "s" : ""}{" "}
                        scheduled
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="px-3 py-1 text-sm font-button font-semibold rounded-full bg-brand-wool text-brand-fawn border border-brand-fawn">
                    {eventCount} event{eventCount !== 1 ? "s" : ""}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 transition-colors text-muted-foreground group-hover:text-brand-red-gum" />
                  ) : (
                    <ChevronRight className="w-5 h-5 transition-colors text-muted-foreground group-hover:text-brand-red-gum" />
                  )}
                </div>
              </button>

              {/* Events Content - Animated */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isExpanded
                    ? "opacity-100 max-h-[2000px]"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-4 border-t-2 lg:p-6 bg-white border-foreground/10">
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                    {sortedEvents.map((eventTime, eventIndex) => (
                        <Card
                          key={`${eventTime.id ?? eventTime.slug}-${eventIndex}`}
                          className="overflow-hidden bg-white border-2 shadow-lg transition-all duration-200 cursor-pointer group border-foreground/20 hover:shadow-xl"
                          onClick={() => handleEventClick(eventTime, eventTime)}
                        >
                          <div className="overflow-hidden relative aspect-video">
                            <Img
                              data={
                                eventTime.image?.permalink
                                  ? eventTime.image
                                  : eventTime.grid_image
                              }
                              fill
                              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 20vw"
                              className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-200 from-black/20 group-hover:opacity-100" />
                          </div>
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <h5 className="transition-colors level-4 line-clamp-2 group-hover:text-brand-red-gum">
                                {eventTime.override_title || eventTime.title}
                              </h5>

                              <div className="space-y-2">
                                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                  <Icon icon="clock" size={16} />
                                  <span className="font-bold">
                                    {eventTime.all_day
                                      ? "All Day"
                                      : `${formatTime(eventTime.start_time)}${
                                          eventTime.end_time &&
                                          !eventTime.hide_end_time
                                            ? ` - ${formatTime(
                                                eventTime.end_time
                                              )}`
                                            : ""
                                        }`}
                                  </span>
                                </div>

                                {eventTime.location && (
                                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                    <MapPin className="w-4 h-4" />
                                    <span className="line-clamp-1">
                                      {eventTime.location}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
                                {eventTime.override_short ??
                                  eventTime.short_description}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sortedDates.length === 0 && (
        <div className="py-12 text-center">
          <div className="flex justify-center items-center p-4 mx-auto mb-4 w-16 h-16 rounded-full bg-card">
            <Icon icon="events" size={32} strokeWidth={1.7} />
          </div>
          <h4 className="mb-2 level-3">No events scheduled</h4>
          <p className="text-muted-foreground">
            Check back later for updates to the schedule.
          </p>
        </div>
      )}

      {/* Modal/Drawer for Event Details */}
      {selectedEvent && (
        <>
          {isLargeScreen ? (
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogContent className="min-h-[500px] max-h-screen">
                <EventDetails
                  mapIsActive={mapIsActive}
                  userId={userId}
                  data={selectedEvent.data}
                  type={selectedEvent.type}
                  title={selectedEvent.title}
                  categories={selectedEvent.data?.categories}
                  locations={selectedEvent.data?.locations}
                  showAddToMyShow={true}
                  image={selectedEvent.image}
                  eventTimes={[selectedEvent.eventTime]}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Drawer
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              shouldScaleBackground={false}
            >
              <DrawerContent>
                <EventDetails
                  userId={userId}
                  data={selectedEvent.data}
                  type={selectedEvent.type}
                  title={selectedEvent.title}
                  categories={selectedEvent.data?.categories}
                  locations={selectedEvent.data?.locations}
                  showAddToMyShow={true}
                  image={selectedEvent.image}
                  eventTimes={[selectedEvent.eventTime]}
                />
              </DrawerContent>
            </Drawer>
          )}
        </>
      )}
    </div>
  );
}
